import { useEffect, type ReactNode } from "react";
import { ChevronDown, Flag, Mail, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LOG_LABEL, LOG_OPTIONS, type LogOption } from "@/components/activity/logOptions";
import { isLogShortcut } from "./recordFormat";

/**
 * The action row every record page shares: Log activity (or press L), Email,
 * Next step, then the page's own buttons (reminder bell, Edit, the ... menu).
 */
export function ActionBar({ onLog, email, onNextStep, children }: {
  onLog: (type: LogOption) => void; email?: string | null; onNextStep: () => void; children?: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement as HTMLElement | null;
      const dialogOpen = !!document.querySelector('[role="dialog"][data-state="open"], [role="menu"][data-state="open"]');
      if (el?.closest?.("#record-activity")) return; // anywhere inside the activity panel
      if (isLogShortcut(e, el?.tagName, !!el?.isContentEditable, dialogOpen)) {
        e.preventDefault();
        onLog("call");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onLog]);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" className="gap-1.5">
            <Plus className="h-3.5 w-3.5" />
            Log activity
            <kbd className="ml-0.5 rounded border border-current/30 px-1 text-[10px] font-semibold opacity-70">L</kbd>
            <ChevronDown className="h-3 w-3 opacity-70" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {LOG_OPTIONS.map((o) => <DropdownMenuItem key={o} onSelect={() => onLog(o)}>{LOG_LABEL[o]}</DropdownMenuItem>)}
        </DropdownMenuContent>
      </DropdownMenu>
      {email && (
        <Button asChild size="sm" variant="outline" className="gap-1.5">
          <a href={`mailto:${email}`}><Mail className="h-3.5 w-3.5" />Email</a>
        </Button>
      )}
      <Button size="sm" variant="outline" className="gap-1.5" onClick={onNextStep}>
        <Flag className="h-3.5 w-3.5" />Next step
      </Button>
      {children}
    </>
  );
}
